import { SpecialPrices } from '~/lib/data-contract/salesforce';
import { ascertainNumber } from '~/shared/utils/numbers';

export const interpolateSalesforceText = (
    template: string,
    values: (string | number)[],
): string => {
    let text = template;

    values.forEach((value, index) => {
        const regex = new RegExp(`{${index}(?:,00)?}`, 'g');
        // Replace {X,00} or {X} tokens with matching value.
        text = text.replace(regex, String(value));
    });

    return text;
};

/**
 * The subset of a Special Price campaign needed to evaluate its threshold. Accepts both the
 * raw Salesforce shape (string amounts) and our aggregated shape (parsed numbers).
 */
export type SpecialPriceThreshold = Pick<
    SpecialPrices,
    'isAmountThreshold' | 'basketAmountThreshold' | 'basketNumberOfProductsThreshold'
> & {
    amountInBasket?: string | number;
    quantityInBasket?: string | number;
};

/**
 * A Special Price campaign only kicks in once the basket has met the campaign threshold —
 * either a total amount or a number of products.
 */
export const hasMetSpecialPriceRequirements = (promotion?: SpecialPriceThreshold): boolean => {
    const {
        isAmountThreshold = true,
        basketAmountThreshold = 0,
        basketNumberOfProductsThreshold = 0,
        amountInBasket = 0,
        quantityInBasket = 0,
    } = promotion || {};

    const currentValue = isAmountThreshold
        ? ascertainNumber(amountInBasket, { parser: parseFloat })
        : ascertainNumber(quantityInBasket);

    const thresholdValue = isAmountThreshold
        ? basketAmountThreshold
        : basketNumberOfProductsThreshold;

    return currentValue >= thresholdValue;
};

/**
 * Special Pricing is *applied* when a campaign was resolved for the line (isActive) **and** the
 * basket has met that campaign's requirements. An inactive or unmet campaign changes nothing for
 * the customer, so it must not be treated as applied.
 *
 * Pass the aggregated promotion from `useAppliedSpecialPricing` rather than a line item's own
 * `c_specialPrices`: only the aggregate reflects the current state of the basket.
 */
export const isSpecialPriceApplied = (promotion?: SpecialPriceThreshold): boolean =>
    !!promotion && hasMetSpecialPriceRequirements(promotion);
